import { ApiError } from './authService'

export interface Product {
  id: string
  name: string
  description: string
  price: number
  imageUrl: string
  sizes: string[]
  maxPerUser: number | null
  isActive: boolean
  requiresDelivery: boolean
  shippingCost: number
  createdAt?: string
  updatedAt?: string
}

export interface OrderProductSummary {
  id: string
  name: string
  price: number
  description?: string
  imageUrl?: string
  sizes?: string[]
}

export interface Order {
  id: number
  userId: number
  productId: string
  size: string | null
  price: number
  status: string
  createdAt: string
  product?: OrderProductSummary
}

export interface DeliveryInfo {
  name: string
  phone: string
  address: string
  city: string
  postalCode: string
  notes?: string
}

export interface PurchasePayload {
  items: { productId: string; size: string }[]
  expectedTotal: number
  deliveryInfo?: DeliveryInfo
}

export interface CartItem {
  product: Product
  size: string
}

export const productTotal = (product: Product) =>
  product.price + (product.requiresDelivery ? product.shippingCost : 0)

export interface PurchaseResponse {
  success: boolean
  message: string
  orders?: {
    id: number
    productId: string
    userId: number
    price: number
    size: string | null
    status: string
  }[]
  remainingCoins?: number
}

async function apiRequest<T>(url: string, options?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  })

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }))
    throw new ApiError(res.status, error.message || 'Request failed')
  }

  const text = await res.text()
  if (!text) return {} as T
  return JSON.parse(text)
}

export async function fetchProducts(params?: { activeOnly?: boolean }): Promise<Product[]> {
  const query = params?.activeOnly ? `?activeOnly=${params.activeOnly}` : ''
  const products = await apiRequest<Product[]>(`/api/shop/products${query}`)
  return products.map((product) => ({
    ...product,
    description: product.description ?? '',
    imageUrl: product.imageUrl ?? '',
    sizes: product.sizes ?? [],
    shippingCost: product.shippingCost ?? 0,
  }))
}

export async function fetchUserOrders(userId: number): Promise<Order[]> {
  return apiRequest<Order[]>(`/api/users/${userId}/orders`)
}

export async function purchaseProduct(payload: PurchasePayload): Promise<PurchaseResponse> {
  return apiRequest<PurchaseResponse>('/api/shop/purchase', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}
